import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ChatChannelResponse, ChatChannelMemberResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const MEMBER_PAGE_SIZE = 100;

interface ChannelData {
  channel: ChatChannelResponse;
  members: ChatChannelMemberResponse[];
  memberCount: number;
}

interface UseChatChannelReturn {
  channel: ChatChannelResponse | null;
  members: ChatChannelMemberResponse[];
  memberCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  join: () => Promise<boolean>;
  leave: () => Promise<boolean>;
}

export function useChatChannel(channelId: string | null): UseChatChannelReturn {
  const queryClient = useQueryClient();
  const channelQueryKey = queryKeys.chat.channel(channelId!);

  const { data, isLoading, error, refetch } = useQuery<ChannelData>({
    queryKey: channelQueryKey,
    queryFn: async () => {
      const [channelResult, membersResult] = await Promise.all([
        api.getChatChannel(channelId!),
        api.getChatChannelMembers(channelId!, { limit: MEMBER_PAGE_SIZE }),
      ]);
      return {
        channel: channelResult.channel,
        members: membersResult.members,
        memberCount: membersResult.total,
      };
    },
    enabled: !!channelId,
  });

  const joinMut = useMutation({
    mutationFn: () => api.joinChatChannel(channelId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: channelQueryKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.chat.channels() });
    },
  });

  const leaveMut = useMutation({
    mutationFn: () => api.leaveChatChannel(channelId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.chat.channels() });
    },
  });

  return {
    channel: data?.channel ?? null,
    members: data?.members ?? [],
    memberCount: data?.memberCount ?? 0,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load channel' : null,
    refresh: () => {
      refetch();
    },
    join: async (): Promise<boolean> => {
      if (!channelId) return false;
      try {
        await joinMut.mutateAsync();
        return true;
      } catch {
        return false;
      }
    },
    leave: async (): Promise<boolean> => {
      if (!channelId) return false;
      try {
        await leaveMut.mutateAsync();
        return true;
      } catch {
        return false;
      }
    },
  };
}
